import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { type HostAddressable, machineHost } from "@/lib/machine-host";

/** A machine reduced to the fields needed for a clipboard line. */
export interface ClipboardMachine extends HostAddressable {
  name: string;
}

/**
 * Canonical clipboard line for a machine: `"<name>,<host>"` (or just the name),
 * with the host resolved tailnet-first so full machine objects copy the address
 * that actually reaches the machine, not the (often firewalled) public ip.
 * Single source of truth shared by the copy buttons and the acquire flows.
 */
export function machineClipboardLine(machine: ClipboardMachine): string {
  const host = machineHost(machine);
  return host ? `${machine.name},${host}` : machine.name;
}

/** Copy machines as newline-joined `"<name>,<ip>"` lines; toast-free. */
async function copyMachines(machines: ClipboardMachine[]): Promise<boolean> {
  if (machines.length === 0) return false;
  return copyText(machines.map(machineClipboardLine).join("\n"));
}

/** Copy machines + a success/error toast (one machine shows the line, many a count). */
export async function copyMachinesWithToast(machines: ClipboardMachine[]): Promise<boolean> {
  if (machines.length === 0) return false;
  if (!(await copyMachines(machines))) {
    toast.error("Copy failed", { duration: 1_500 });
    return false;
  }
  const [firstMachine] = machines;
  const message =
    machines.length === 1 && firstMachine
      ? `Copied ${machineClipboardLine(firstMachine)}`
      : `Copied ${machines.length} machine${machines.length === 1 ? "" : "s"}`;
  toast.success(message, { duration: 1_500 });
  return true;
}
